import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errText } from "@/lib/api";
import { useCustomer } from "@/lib/customer";
import { fmtDate, fmtMoney, fmtNumber } from "@/lib/format";
import { LdBadge, Mono, Stat } from "@/components/Badges";
import { toast } from "sonner";

export default function Dashboard() {
  const { customerId, onExpiryClock, stages } = useCustomer();
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get("/dashboard")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(errText(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const c = data.counts;

  return (
    <div className="space-y-5" data-testid="dashboard-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Dashboard</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Counted per PO, derived from the records as they stand now. A PO sits in the stage of its most behind line.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <Stat label="Open POs" value={fmtNumber(c.openPos)} colour="grey" testId="stat-open-pos" onClick={() => navigate("/pos?status=open")} />
        <Stat label="Closed POs" value={fmtNumber(c.closed)} colour="grey" testId="stat-closed" onClick={() => navigate("/pos?status=closed")} />
        <Stat label={onExpiryClock ? "POs past expiry" : "POs with LD running"} value={fmtNumber(c.ldRunning)} colour="red" testId="stat-ld-running" onClick={() => navigate("/pos?ldColour=red")} />
        <Stat label={onExpiryClock ? "POs near expiry" : "POs at risk"} value={fmtNumber(c.atRisk)} colour="amber" testId="stat-at-risk" onClick={() => navigate("/pos?ldColour=amber")} />
        {!onExpiryClock && (
          <Stat label="Exemptions raised" value={fmtNumber(c.exemptionsRaised)} colour="green" testId="stat-exemptions" onClick={() => navigate("/pos")} />
        )}        <Stat label="POs needing a chase" value={fmtNumber(c.followUpsDue)} colour="amber" testId="stat-followups" onClick={() => navigate("/tasks")} />
        {c.openPortalRejections > 0 && (
          <Stat label="Open portal rejections" value={fmtNumber(c.openPortalRejections)} colour="red" testId="stat-rejections" onClick={() => navigate("/tasks")} />
        )}
        <Stat label={`Held at ${stages?.[0]?.label || "the first stage"}`} value={fmtNumber(c.heldAtSiteReadiness)} colour="amber" testId="stat-site-held" onClick={() => navigate("/pos?status=open")} />
        {!onExpiryClock && (
          <>
            <Stat label="BOM pending" value={fmtNumber(c.bomPending)} colour="amber" testId="stat-bom-pending" onClick={() => navigate("/pos?stage=2")} />
            <Stat label="Exemptions pending" value={fmtNumber(c.exemptionsPending)} colour="red" testId="stat-exemptions-pending" onClick={() => navigate("/tasks")} />
            <Stat label="LD exposure" value={fmtMoney(c.ldExposure)} colour="red" testId="stat-ld-exposure" onClick={() => navigate("/pos?ldColour=red")} />
          </>
        )}
      </div>

      <section>
        <h2 className="mb-2 text-lg font-medium tracking-tight">Per person</h2>
        <div className="panel overflow-x-auto">
          <table className="w-full min-w-[900px] text-left">
            <thead className="border-b border-border bg-secondary/60 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="cell font-medium">Person</th>
                {data.stageGroups.map((g) => (
                  <th key={g} className="cell font-medium">{g}</th>
                ))}
                <th className="cell font-medium">Open POs</th>
                <th className="cell font-medium">{onExpiryClock ? "Past expiry" : "LD running"}</th>
                <th className="cell font-medium">POs to chase</th>
                <th className="cell font-medium">Next action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.people.map((p) => (
                <tr key={p.owner} data-testid={`dashboard-person-${p.owner}`}>
                  <td className="cell font-medium">{p.owner}</td>
                  {data.stageGroups.map((g) => (
                    <td key={g} className="cell font-mono">{fmtNumber(p.groups[g] || 0)}</td>
                  ))}
                  <td className="cell font-mono">{fmtNumber(p.open)}</td>
                  <td className="cell font-mono">{fmtNumber(p.ldRunning)}</td>
                  <td className="cell font-mono">{fmtNumber(p.followUpsDue)}</td>
                  <td className="cell max-w-[280px]">
                    {p.nextAction ? (
                      <button
                        onClick={() => navigate(`/po-history/${p.nextLineId}`)}
                        className="block max-w-[260px] truncate text-left hover:underline"
                        title={p.nextAction}
                        data-testid={`dashboard-next-action-${p.owner}`}
                      >
                        {p.nextAction} · due {fmtDate(p.nextDue)}
                      </button>
                    ) : (
                      <span className="text-muted-foreground">Nothing due</span>
                    )}
                  </td>
                </tr>
              ))}
              {data.people.length === 0 && (
                <tr>
                  <td colSpan={10} className="p-8 text-center text-sm text-muted-foreground">No PO on record yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-lg font-medium tracking-tight">POs aged over the completion TAT, oldest first</h2>
        <div className="panel overflow-x-auto">
          <table className="w-full min-w-[820px] text-left">
            <thead className="border-b border-border bg-secondary/60 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                {["Ageing", "PO number", "PO date", "Branch", "Lines", "Owner", "Stage", onExpiryClock ? "Expiry" : "LD"].map((h) => (
                  <th key={h} className="cell font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.aged.map((po) => (
                <tr
                  key={po.key}
                  className="cursor-pointer hover:bg-secondary/50"
                  onClick={() => navigate(`/po-history/${po.lineId}`)}
                  data-testid={`dashboard-aged-${po.lineId}`}
                >
                  <td className="cell font-mono">{po.ageing}d</td>
                  <td className="cell font-mono">{po.poNumber}</td>
                  <td className="cell font-mono">{fmtDate(po.poDate)}</td>
                  <td className="cell">{po.branchName || "—"}</td>
                  <td className="cell font-mono">{fmtNumber(po.lineCount)}</td>
                  <td className="cell">{po.owner}</td>
                  <td className="cell">{po.stageLabel}</td>
                  <td className="cell">
                    <LdBadge colour={po.ldColour} text={po.ldStatus} />
                  </td>
                </tr>
              ))}
              {data.aged.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-sm text-muted-foreground" data-testid="dashboard-aged-empty">
                    No PO is aged past the completion TAT.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
